import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, Bed, Sparkles, Edit, Trash2, Settings, Layers, Copy, Edit2, Loader2, 
  IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Import refactored components
import PricingTab from '@/components/room/PricingTab';
import { amenityIcons, iconOptions, getAmenityIcon, getStatusColor } from '@/components/room/constants';

export default function RoomManagement({ user: propUser, isTab, tabId }) {
  // Get user from props or localStorage (for tab mode)
  const [user, setUser] = useState(() => {
    if (propUser) return propUser;
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : { role: 'hotel_admin' };
    } catch {
      return { role: 'hotel_admin' };
    }
  });
  const { openTab } = useTabs();
  
  // Derive isSuperAdmin early to use in state initialization
  const isSuperAdmin = user.role === 'super_admin';
  const canManageRooms = ['super_admin', 'hotel_admin', 'manager'].includes(user.role);
  
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  // Only Super Admin can manage room types (master data)
  const [activeTab, setActiveTab] = useState(isSuperAdmin ? 'room-types' : 'rooms');
  
  // Room dialog states
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [roomFormData, setRoomFormData] = useState({
    room_number: '',
    room_type_id: '',
    block_name: '',
    floor: '',
    capacity: '',
    smoking_allowed: false,
    amenity_ids: [],
    photos: [],
    // Room size
    room_size: '',
    room_size_unit: 'sqft',
    // Guest capacity overrides
    override_adults: '',
    override_children: '',
    max_adults: '',
    max_children: ''
  });
  const [bulkFormData, setBulkFormData] = useState({
    room_ranges: '',
    room_type_id: '',
    block_name: '',
    floor_prefix: true,
    smoking_allowed: false
  });
  
  // Room Type dialog states
  const [roomTypeDialogOpen, setRoomTypeDialogOpen] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [roomTypeFormData, setRoomTypeFormData] = useState({
    name: '',
    description: ''
  });
  
  // Amenity dialog states
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [amenityFormData, setAmenityFormData] = useState({ name: '', icon: 'other', description: '' });
  
  // Bed Type dialog states
  const [bedTypeDialogOpen, setBedTypeDialogOpen] = useState(false);
  const [editingBedType, setEditingBedType] = useState(null);
  const [bedTypeFormData, setBedTypeFormData] = useState({ name: '', description: '' });
  
  // Hotel-specific room types
  const [hotelRoomTypes, setHotelRoomTypes] = useState([]);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // Filters
  const [filterRoomType, setFilterRoomType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Multi-select for bulk edit
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [bulkPriceDialogOpen, setBulkPriceDialogOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    price_per_night: '',
    room_type_id: '',
    status: '',
    capacity: '',
    amenity_ids: null // null = don't change
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [roomsRes, roomTypesRes, amenitiesRes, bedTypesRes, pricingRes] = await Promise.all([
        api.get('/rooms'),
        api.get('/room-types'),
        api.get('/amenities'),
        api.get('/bed-types?active_only=false'),
        api.get('/room-pricing').catch(() => ({ data: [] }))
      ]);
      setRooms(roomsRes.data);
      setRoomTypes(roomTypesRes.data);
      setAmenities(amenitiesRes.data);
      setBedTypes(bedTypesRes.data);
      setPricingRules(pricingRes.data || []);
      
      // Fetch hotel-specific room types if user has a hotel
      if (user.hotel_id) {
        try {
          const hotelRoomTypesRes = await api.get(`/companies/hotels/${user.hotel_id}/assigned-room-types`);
          setHotelRoomTypes(hotelRoomTypesRes.data.assigned_room_types || []);
        } catch (err) {
          // Fallback to all room types if hotel-specific not available
          setHotelRoomTypes(roomTypesRes.data.filter(rt => rt.is_active));
        }
      } else {
        // Super admin sees all room types
        setHotelRoomTypes(roomTypesRes.data.filter(rt => rt.is_active));
      }
    } catch (error) {
      console.error('Failed to load room data:', error);
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };
  
  const activeAmenities = amenities.filter(a => a.is_active);
  const activeBedTypes = bedTypes.filter(b => b.is_active);

  // ==================== ROOM TYPE HANDLERS ====================
  
  const handleRoomTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: roomTypeFormData.name,
        description: roomTypeFormData.description || null,
        base_price: 0,
        max_capacity: 2,
        amenities: [],
        amenity_ids: []
      };

      if (editingRoomType) {
        await api.put(`/room-types/${editingRoomType.id}`, data);
        toast.success('Room type updated successfully');
      } else {
        await api.post('/room-types', data);
        toast.success('Room type created successfully');
      }
      
      setRoomTypeDialogOpen(false);
      resetRoomTypeForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save room type'));
    }
  };

  const resetRoomTypeForm = () => {
    setEditingRoomType(null);
    setRoomTypeFormData({ name: '', description: '' });
  };

  const openNewRoomTypeDialog = () => {
    resetRoomTypeForm();
    setRoomTypeDialogOpen(true);
  };

  const handleEditRoomType = (roomType) => {
    setEditingRoomType(roomType);
    setRoomTypeFormData({
      name: roomType.name,
      description: roomType.description || ''
    });
    setRoomTypeDialogOpen(true);
  };

  // ==================== ROOM HANDLERS ====================
  
  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    const roomType = roomTypes.find(rt => rt.id === roomFormData.room_type_id);
    
    try {
      const data = {
        room_number: roomFormData.room_number,
        room_type_id: roomFormData.room_type_id,
        room_type: roomType?.name || '',
        block_name: roomFormData.block_name || null,
        price_per_night: roomType?.base_price || 0,
        floor: parseInt(roomFormData.floor),
        capacity: parseInt(roomFormData.capacity),
        smoking_allowed: roomFormData.smoking_allowed || false,
        amenity_ids: roomFormData.amenity_ids || [],
        photos: roomFormData.photos || [],
        bed_type_ids: []
      };

      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, data);
        toast.success('Room updated successfully');
      } else {
        await api.post('/rooms', data);
        toast.success('Room added successfully');
      }
      
      setRoomDialogOpen(false);
      resetRoomForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save room'));
    }
  };

  const handleBulkRoomSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/rooms/bulk', {
        room_ranges: bulkFormData.room_ranges,
        room_type_id: bulkFormData.room_type_id,
        block_name: bulkFormData.block_name || null,
        floor_prefix: bulkFormData.floor_prefix,
        amenity_ids: [],
        smoking_allowed: bulkFormData.smoking_allowed || false,
        bed_type_ids: []
      });
      
      toast.success(response.data.message);
      setRoomDialogOpen(false);
      setBulkMode(false);
      resetBulkForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create rooms'));
    }
  };

  const resetRoomForm = () => {
    setEditingRoom(null);
    setRoomFormData({
      room_number: '',
      room_type_id: '',
      block_name: '',
      floor: '',
      capacity: '',
      smoking_allowed: false,
      amenity_ids: [],
      photos: [],
      room_size: '',
      room_size_unit: 'sqft',
      override_adults: '',
      override_children: '',
      max_adults: '',
      max_children: ''
    });
  };

  const resetBulkForm = () => {
    setBulkFormData({
      room_ranges: '',
      room_type_id: '',
      block_name: '',
      floor_prefix: true,
      smoking_allowed: false
    });
  };

  const handleEditRoom = (room) => {
    openTab({
      type: TAB_TYPES.ROOM_EDIT,
      id: `room-edit-${room.id}`,
      title: `Edit Room ${room.room_number}`,
      data: { room, onComplete: loadData }
    });
  };

  const openNewRoomDialog = (bulk = false) => {
    resetRoomForm();
    resetBulkForm();
    setBulkMode(bulk);
    setRoomDialogOpen(true);
  };

  const updateRoomStatus = async (roomId, newStatus) => {
    try {
      await api.patch(`/rooms/${roomId}`, { status: newStatus });
      toast.success(`Room status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update room status'));
    }
  };

  // ==================== AMENITY HANDLERS ====================
  
  const handleAmenitySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAmenity) {
        await api.put(`/amenities/${editingAmenity.id}`, amenityFormData);
        toast.success('Amenity updated successfully');
      } else {
        await api.post('/amenities', amenityFormData);
        toast.success('Amenity created successfully');
      }
      setAmenityDialogOpen(false);
      setEditingAmenity(null);
      setAmenityFormData({ name: '', icon: 'other', description: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save amenity'));
    }
  };

  const handleEditAmenity = (amenity) => {
    setEditingAmenity(amenity);
    setAmenityFormData({ name: amenity.name, icon: amenity.icon || 'other', description: amenity.description || '' });
    setAmenityDialogOpen(true);
  };

  const handleToggleAmenityActive = async (amenity) => {
    try {
      await api.put(`/amenities/${amenity.id}`, { ...amenity, is_active: !amenity.is_active });
      toast.success(`Amenity ${amenity.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update amenity');
    }
  };

  // ==================== BED TYPE HANDLERS ====================
  
  const handleBedTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBedType) {
        await api.put(`/bed-types/${editingBedType.id}`, bedTypeFormData);
        toast.success('Bed type updated successfully');
      } else {
        await api.post('/bed-types', bedTypeFormData);
        toast.success('Bed type created successfully');
      }
      setBedTypeDialogOpen(false);
      setEditingBedType(null);
      setBedTypeFormData({ name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save bed type'));
    }
  };

  const handleEditBedType = (bedType) => {
    setEditingBedType(bedType);
    setBedTypeFormData({ name: bedType.name, description: bedType.description || '' });
    setBedTypeDialogOpen(true);
  };

  const handleToggleBedTypeActive = async (bedType) => {
    try {
      await api.put(`/bed-types/${bedType.id}`, { ...bedType, is_active: !bedType.is_active });
      toast.success(`Bed type ${bedType.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update bed type');
    }
  };

  // ==================== DELETE HANDLERS ====================
  
  const confirmDelete = (item, type) => {
    setDeleteTarget(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const url = deleteType === 'amenity' ? `/amenities/${deleteTarget.id}` : `/${deleteType}s/${deleteTarget.id}`;
      await api.delete(url);
      toast.success(`${deleteType} deleted successfully`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to delete ${deleteType}`));
    }
  };

  // ==================== HELPERS ====================

  // Filtered rooms
  const filteredRooms = rooms.filter(room => {
    if (filterRoomType !== 'all' && room.room_type_id !== filterRoomType) return false;
    if (filterStatus !== 'all' && room.status !== filterStatus) return false;
    if (filterFloor !== 'all' && room.floor.toString() !== filterFloor) return false;
    if (searchQuery && !room.room_number.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const uniqueFloors = [...new Set(rooms.map(r => r.floor))].sort((a, b) => a - b);

  const toggleRoomSelection = (roomId) => {
    setSelectedRooms(prev => 
      prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]
    );
  };

  const selectAllFiltered = () => {
    if (selectedRooms.length === filteredRooms.length) {
      setSelectedRooms([]);
    } else {
      setSelectedRooms(filteredRooms.map(r => r.id));
    }
  };

  const handleBulkEdit = async () => {
    if (selectedRooms.length === 0) return;
    const update = {};
    if (bulkEditData.price_per_night) update.price_per_night = parseFloat(bulkEditData.price_per_night);
    if (bulkEditData.room_type_id) {
      update.room_type_id = bulkEditData.room_type_id;
      const rt = roomTypes.find(r => r.id === bulkEditData.room_type_id);
      if (rt) update.room_type = rt.name;
    }
    if (bulkEditData.status) update.status = bulkEditData.status;
    if (bulkEditData.capacity) update.capacity = parseInt(bulkEditData.capacity);
    if (bulkEditData.amenity_ids !== null) update.amenity_ids = bulkEditData.amenity_ids;

    if (Object.keys(update).length === 0) {
      toast.error('No changes to apply');
      return;
    }
    try {
      await Promise.all(
        selectedRooms.map(roomId => api.put(`/rooms/${roomId}`, update))
      );
      toast.success(`Updated ${selectedRooms.length} room(s)`);
      setSelectedRooms([]);
      setBulkPriceDialogOpen(false);
      setBulkEditData({ price_per_night: '', room_type_id: '', status: '', capacity: '', amenity_ids: null });
      loadData();
    } catch (err) {
      toast.error('Failed to update rooms');
    }
  };

  const toggleRoomAmenity = (amenityId) => {
    setRoomFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  const [uploadingRoomPhoto, setUploadingRoomPhoto] = useState(false);

  const handleRoomPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingRoomPhoto(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams({ photo_type: 'room', entity_id: editingRoom?.id || 'new' });
        const res = await api.post(`/upload/portal-photo?${params.toString()}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setRoomFormData(prev => ({
          ...prev,
          photos: [...(prev.photos || []), res.data.photo_url]
        }));
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingRoomPhoto(false);
      e.target.value = '';
    }
  };

  const removeRoomPhoto = (index) => {
    setRoomFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const getRoomTypeName = (roomTypeId) => {
    const rt = roomTypes.find(r => r.id === roomTypeId);
    return rt?.name || 'Unknown';
  };

  const getBedTypeName = (bedTypeId) => {
    const bt = bedTypes.find(b => b.id === bedTypeId);
    return bt?.name || '-';
  };

  return (
    <div className="space-y-6" data-testid="room-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>
            {isSuperAdmin ? 'Master Data' : 'Room Management'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isSuperAdmin ? 'Manage room types, amenities, and bed types' : 'Manage rooms and pricing'}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-3' : 'grid-cols-2'} max-w-3xl`}>
          {!isSuperAdmin && (
            <TabsTrigger value="rooms" className="flex items-center gap-2">
              <Bed className="h-4 w-4" />
              Rooms
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="room-types" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Room Types
            </TabsTrigger>
          )}
          {!isSuperAdmin && (
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <IndianRupee className="h-4 w-4" />
              Pricing
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="bed-types" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Bed Types
            </TabsTrigger>
          )}
        </TabsList>

        {/* ROOMS TAB */}
        <TabsContent value="rooms" className="mt-6">
          {canManageRooms && (
            <div className="flex flex-wrap gap-2 mb-4">
              <Button onClick={() => {
                openTab({
                  type: TAB_TYPES.ROOM_ADD,
                  id: `room-add-${Date.now()}`,
                  title: 'Add Room',
                  data: { onComplete: loadData }
                });
              }} className="bg-gradient-orange hover:opacity-90 text-white" data-testid="add-room-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Room
              </Button>
              <Button onClick={() => openNewRoomDialog(true)} variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="bulk-add-rooms-button">
                <Copy className="h-4 w-4 mr-2" />
                Bulk Add
              </Button>
              {selectedRooms.length > 0 && (
                <Button 
                  variant="outline" 
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => setBulkPriceDialogOpen(true)}
                  data-testid="bulk-edit-btn"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Bulk Edit ({selectedRooms.length})
                </Button>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="room-filters">
            <Input 
              placeholder="Search room..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-40 h-8 text-sm"
              data-testid="room-search-input"
            />
            <Select value={filterRoomType} onValueChange={setFilterRoomType}>
              <SelectTrigger className="w-36 h-8 text-sm" data-testid="filter-room-type">
                <SelectValue placeholder="Room Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {roomTypes.map(rt => (
                  <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 h-8 text-sm" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="occupied">Occupied</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterFloor} onValueChange={setFilterFloor}>
              <SelectTrigger className="w-28 h-8 text-sm" data-testid="filter-floor">
                <SelectValue placeholder="Floor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Floors</SelectItem>
                {uniqueFloors.map(f => (
                  <SelectItem key={f} value={f.toString()}>Floor {f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canManageRooms && filteredRooms.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAllFiltered} className="h-8 text-xs" data-testid="select-all-btn">
                {selectedRooms.length === filteredRooms.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filteredRooms.length} of {rooms.length} rooms</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
            </div>
          ) : filteredRooms.length === 0 ? (
            <Card className="border-orange-100">
              <CardContent className="py-12 text-center">
                <Bed className="h-12 w-12 mx-auto text-orange-300 mb-4" />
                <p className="text-gray-500">{rooms.length === 0 ? 'No rooms added yet' : 'No rooms match filters'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filteredRooms.map((room) => {
                const isSelected = selectedRooms.includes(room.id);
                return (
                <Card key={room.id} className={`border-orange-100 hover:border-orange-300 hover:shadow-md transition-all ${isSelected ? 'ring-2 ring-blue-400 border-blue-300' : ''}`} data-testid={`room-card-${room.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {canManageRooms && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRoomSelection(room.id)}
                            className="mt-0.5"
                            data-testid={`room-select-${room.id}`}
                          />
                        )}
                        <div>
                          <h3 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                            {room.room_number}
                          </h3>
                          <p className="text-xs text-gray-500">{room.room_type}</p>
                        </div>
                      </div>
                      <Badge className={`${getStatusColor(room.status)} text-xs`}>
                        {room.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Floor</span>
                        <span className="font-medium">{room.floor}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Price</span>
                        <span className="font-bold text-orange-600">₹{room.price_per_night?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Capacity</span>
                        <span className="font-medium">{room.capacity}</span>
                      </div>
                      {room.block_name && (
                        <div className="flex justify-between">
                          <span>Block</span>
                          <span className="font-medium">{room.block_name}</span>
                        </div>
                      )}
                    </div>
                    
                    {canManageRooms && (
                      <div className="flex gap-1 mt-3 pt-2 border-t border-orange-100">
                        <Button variant="ghost" size="sm" onClick={() => handleEditRoom(room)} className="flex-1 h-7 text-xs">
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        {room.status !== 'occupied' && (
                          <Select onValueChange={(value) => updateRoomStatus(room.id, value)}>
                            <SelectTrigger className="h-7 text-xs flex-1">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="available">Available</SelectItem>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="reserved">Reserved</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}

          {/* Bulk Edit Dialog */}
          <Dialog open={bulkPriceDialogOpen} onOpenChange={setBulkPriceDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Bulk Edit — {selectedRooms.length} Room(s)</DialogTitle>
                <DialogDescription>
                  Leave fields empty to keep current values
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price per Night</Label>
                    <Input
                      type="number"
                      min="0"
                      value={bulkEditData.price_per_night}
                      onChange={(e) => setBulkEditData(prev => ({ ...prev, price_per_night: e.target.value }))}
                      placeholder="No change"
                      data-testid="bulk-price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={bulkEditData.capacity}
                      onChange={(e) => setBulkEditData(prev => ({ ...prev, capacity: e.target.value }))}
                      placeholder="No change"
                      data-testid="bulk-capacity-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room Type</Label>
                    <Select value={bulkEditData.room_type_id} onValueChange={(v) => setBulkEditData(prev => ({ ...prev, room_type_id: v }))}>
                      <SelectTrigger data-testid="bulk-room-type-select">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypes.map(rt => (
                          <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={bulkEditData.status} onValueChange={(v) => setBulkEditData(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger data-testid="bulk-status-select">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="occupied">Occupied</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="reserved">Reserved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Amenities */}
                {activeAmenities.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Amenities</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-6"
                        onClick={() => setBulkEditData(prev => ({
                          ...prev,
                          amenity_ids: prev.amenity_ids === null ? [] : null
                        }))}
                      >
                        {bulkEditData.amenity_ids === null ? 'Enable change' : 'Skip amenities'}
                      </Button>
                    </div>
                    {bulkEditData.amenity_ids !== null ? (
                      <div className="grid grid-cols-3 gap-1.5 max-h-32 overflow-y-auto border rounded-lg p-2">
                        {activeAmenities.map((amenity) => {
                          const isSelected = (bulkEditData.amenity_ids || []).includes(amenity.id);
                          return (
                            <button
                              type="button"
                              key={amenity.id}
                              onClick={() => {
                                setBulkEditData(prev => ({
                                  ...prev,
                                  amenity_ids: isSelected
                                    ? prev.amenity_ids.filter(id => id !== amenity.id)
                                    : [...(prev.amenity_ids || []), amenity.id]
                                }));
                              }}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                isSelected ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'
                              }`}
                            >
                              {amenity.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Amenities will not be changed</p>
                    )}
                  </div>
                )}

                <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                  Rooms: {selectedRooms.map(id => rooms.find(r => r.id === id)?.room_number).filter(Boolean).join(', ')}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkPriceDialogOpen(false)}>Cancel</Button>
                <Button 
                  className="bg-gradient-orange text-white"
                  onClick={handleBulkEdit}
                  data-testid="bulk-edit-save-btn"
                >
                  Update {selectedRooms.length} Room(s)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ROOM TYPES TAB */}
        <TabsContent value="room-types" className="mt-6">
          {canManageRooms && (
            <div className="mb-6">
              <Button onClick={openNewRoomTypeDialog} className="bg-gradient-orange hover:opacity-90 text-white" data-testid="add-room-type-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Room Type
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
            </div>
          ) : roomTypes.length === 0 ? (
            <Card className="border-orange-100">
              <CardContent className="py-12 text-center">
                <Layers className="h-12 w-12 mx-auto text-orange-300 mb-4" />
                <p className="text-gray-500">No room types defined yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roomTypes.map((roomType) => (
                <Card key={roomType.id} className="border-orange-100 hover:border-orange-300 hover:shadow-lg transition-all" data-testid={`room-type-card-${roomType.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg text-gray-800">{roomType.name}</CardTitle>
                        {roomType.description && (
                          <p className="text-xs text-gray-500 mt-1">{roomType.description}</p>
                        )}
                      </div>
                      {canManageRooms && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEditRoomType(roomType)} className="h-8 w-8">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => confirmDelete(roomType, 'room-type')} className="h-8 w-8 text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PRICING TAB */}
        <TabsContent value="pricing" className="mt-6">
          <PricingTab
            pricingRules={pricingRules}
            roomTypes={roomTypes}
            bedTypes={bedTypes}
            amenities={amenities}
            hotelRoomTypes={hotelRoomTypes}
            canManageRooms={canManageRooms}
            loadData={loadData}
          />
        </TabsContent>

        {/* BED TYPES TAB */}
        <TabsContent value="bed-types" className="mt-6">
          {canManageRooms && (
            <div className="mb-6">
              <Button onClick={() => { setEditingBedType(null); setBedTypeFormData({ name: '', description: '' }); setBedTypeDialogOpen(true); }} className="bg-gradient-orange hover:opacity-90 text-white" data-testid="add-bed-type-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Bed Type
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
            </div>
          ) : bedTypes.length === 0 ? (
            <Card className="border-orange-100">
              <CardContent className="py-12 text-center">
                <Bed className="h-12 w-12 mx-auto text-orange-300 mb-4" />
                <p className="text-gray-500">No bed types defined yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {bedTypes.map((bedType) => (
                <Card key={bedType.id} className={`border-indigo-100 transition-all ${!bedType.is_active ? 'opacity-50' : 'hover:border-indigo-300'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Bed className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{bedType.name}</p>
                          {bedType.description && (
                            <p className="text-xs text-gray-500">{bedType.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {canManageRooms && (
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-indigo-100">
                        <Switch checked={bedType.is_active} onCheckedChange={() => handleToggleBedTypeActive(bedType)} />
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditBedType(bedType)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmDelete(bedType, 'bed-type')} className="text-red-500 hover:text-red-700" data-testid={`delete-bed-type-${bedType.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Room Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={(open) => { setRoomDialogOpen(open); if (!open) { resetRoomForm(); resetBulkForm(); setBulkMode(false); } }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editingRoom ? 'Edit Room' : (bulkMode ? 'Bulk Add Rooms' : 'Add New Room')}</DialogTitle>
            <DialogDescription>
              {bulkMode ? 'Create multiple rooms at once' : 'Add a single room'}
            </DialogDescription>
          </DialogHeader>

          {!editingRoom && (
            <div className="flex items-center gap-2 pb-4 border-b">
              <Checkbox id="bulk-mode" checked={bulkMode} onCheckedChange={setBulkMode} />
              <Label htmlFor="bulk-mode" className="text-sm cursor-pointer">Multi-room creation</Label>
            </div>
          )}

          {bulkMode && !editingRoom ? (
            <form onSubmit={handleBulkRoomSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Room Ranges *</Label>
                <Textarea
                  value={bulkFormData.room_ranges}
                  onChange={(e) => setBulkFormData({...bulkFormData, room_ranges: e.target.value})}
                  placeholder="101-108, 201-204"
                  required
                  className="border-orange-200"
                />
                <p className="text-xs text-gray-500">Example: 101-108, 201-204</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Room Type *</Label>
                  <Select value={bulkFormData.room_type_id} onValueChange={(value) => setBulkFormData({...bulkFormData, room_type_id: value})} required>
                    <SelectTrigger className="border-orange-200">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Block Name</Label>
                  <Input
                    value={bulkFormData.block_name}
                    onChange={(e) => setBulkFormData({...bulkFormData, block_name: e.target.value})}
                    placeholder="Block A"
                    className="border-orange-200"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="floor_prefix" checked={bulkFormData.floor_prefix} onCheckedChange={(checked) => setBulkFormData({...bulkFormData, floor_prefix: checked})} />
                <Label htmlFor="floor_prefix" className="text-sm cursor-pointer">Auto-detect floor from room number</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="bulk_smoking" checked={bulkFormData.smoking_allowed} onCheckedChange={(checked) => setBulkFormData({...bulkFormData, smoking_allowed: checked})} />
                <Label htmlFor="bulk_smoking" className="text-sm cursor-pointer">Smoking Allowed</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRoomDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange text-white" disabled={!bulkFormData.room_type_id}>Create Rooms</Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={handleRoomSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Room Number *</Label>
                  <Input value={roomFormData.room_number} onChange={(e) => setRoomFormData({...roomFormData, room_number: e.target.value})} required className="border-orange-200" />
                </div>
                <div className="space-y-2">
                  <Label>Room Type *</Label>
                  <Select value={roomFormData.room_type_id} onValueChange={(value) => setRoomFormData({...roomFormData, room_type_id: value})} required>
                    <SelectTrigger className="border-orange-200">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Block Name</Label>
                <Input value={roomFormData.block_name} onChange={(e) => setRoomFormData({...roomFormData, block_name: e.target.value})} placeholder="Block A" className="border-orange-200" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Floor *</Label>
                  <Input type="number" value={roomFormData.floor} onChange={(e) => setRoomFormData({...roomFormData, floor: e.target.value})} required className="border-orange-200" />
                </div>
                <div className="space-y-2">
                  <Label>Capacity *</Label>
                  <Input type="number" min="1" value={roomFormData.capacity} onChange={(e) => setRoomFormData({...roomFormData, capacity: e.target.value})} required className="border-orange-200" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="smoking" checked={roomFormData.smoking_allowed} onCheckedChange={(checked) => setRoomFormData({...roomFormData, smoking_allowed: checked})} />
                <Label htmlFor="smoking" className="text-sm cursor-pointer">Smoking Allowed</Label>
              </div>

              {/* Amenity Selection */}
              {activeAmenities.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> Amenities</Label>
                  <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto border rounded-lg p-2" data-testid="room-amenity-selector">
                    {activeAmenities.map((amenity) => {
                      const Icon = getAmenityIcon(amenity.icon);
                      const isSelected = (roomFormData.amenity_ids || []).includes(amenity.id);
                      return (
                        <button
                          type="button"
                          key={amenity.id}
                          data-testid={`room-amenity-toggle-${amenity.id}`}
                          onClick={() => toggleRoomAmenity(amenity.id)}
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded border text-xs transition-colors ${
                            isSelected ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <Icon className="h-3 w-3 shrink-0" />
                          {amenity.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400">{(roomFormData.amenity_ids || []).length} selected</p>
                </div>
              )}

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Copy className="h-3 w-3" /> Photos
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="room-photo-upload-btn"
                    onClick={() => document.getElementById('room-photo-upload-input').click()}
                    disabled={uploadingRoomPhoto}
                    className="gap-1"
                  >
                    {uploadingRoomPhoto ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {uploadingRoomPhoto ? 'Uploading...' : 'Upload'}
                  </Button>
                  <input
                    id="room-photo-upload-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleRoomPhotoUpload}
                  />
                  <span className="text-xs text-gray-400 self-center">JPEG, PNG, WebP</span>
                </div>
                {roomFormData.photos?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {roomFormData.photos.map((photo, idx) => (
                      <div key={idx} className="relative group aspect-video rounded overflow-hidden bg-gray-100">
                        <img
                          src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                          alt={`Room photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://placehold.co/200x150?text=Error'; }}
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 h-5 w-5 rounded bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeRoomPhoto(idx)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRoomDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange text-white">{editingRoom ? 'Update' : 'Add Room'}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Room Type Dialog */}
      <Dialog open={roomTypeDialogOpen} onOpenChange={(open) => { setRoomTypeDialogOpen(open); if (!open) resetRoomTypeForm(); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editingRoomType ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRoomTypeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Type Name *</Label>
              <Input value={roomTypeFormData.name} onChange={(e) => setRoomTypeFormData({...roomTypeFormData, name: e.target.value})} placeholder="Deluxe, Suite" required className="border-orange-200" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={roomTypeFormData.description} onChange={(e) => setRoomTypeFormData({...roomTypeFormData, description: e.target.value})} placeholder="Optional description" className="border-orange-200" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRoomTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gradient-orange text-white">{editingRoomType ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Amenity Dialog */}
      <Dialog open={amenityDialogOpen} onOpenChange={(open) => { setAmenityDialogOpen(open); if (!open) { setEditingAmenity(null); setAmenityFormData({ name: '', icon: 'other', description: '' }); } }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editingAmenity ? 'Edit Amenity' : 'Add Amenity'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAmenitySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Amenity Name *</Label>
              <Input value={amenityFormData.name} onChange={(e) => setAmenityFormData({...amenityFormData, name: e.target.value})} placeholder="WiFi, AC, Mini Bar" required className="border-orange-200" />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-5 gap-2">
                {iconOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button key={opt.value} type="button" onClick={() => setAmenityFormData({...amenityFormData, icon: opt.value})} className={`p-3 rounded-lg border-2 transition-all ${amenityFormData.icon === opt.value ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-200'}`}>
                      <Icon className={`h-5 w-5 mx-auto ${amenityFormData.icon === opt.value ? 'text-orange-600' : 'text-gray-500'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={amenityFormData.description} onChange={(e) => setAmenityFormData({...amenityFormData, description: e.target.value})} placeholder="Optional" className="border-orange-200" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAmenityDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gradient-orange text-white">{editingAmenity ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bed Type Dialog */}
      <Dialog open={bedTypeDialogOpen} onOpenChange={(open) => { setBedTypeDialogOpen(open); if (!open) { setEditingBedType(null); setBedTypeFormData({ name: '', description: '' }); } }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editingBedType ? 'Edit Bed Type' : 'Add Bed Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBedTypeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Bed Type Name *</Label>
              <Input value={bedTypeFormData.name} onChange={(e) => setBedTypeFormData({...bedTypeFormData, name: e.target.value})} placeholder="King, Queen, Twin" required className="border-orange-200" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={bedTypeFormData.description} onChange={(e) => setBedTypeFormData({...bedTypeFormData, description: e.target.value})} placeholder="Optional" className="border-orange-200" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBedTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gradient-orange text-white">{editingBedType ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the {deleteType}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
